import { Dispatch } from "redux";
import { getAllCentres, getAllCountries, getSchoolById, getSchoolsByCentre } from "app/shared/api";
import { getExcludedBookingSystemIdsForSchool } from "app/shared/api/bookingSystemExclusions";
import { Centre, Country, SaveSchoolParams, School } from "app/shared/model";
import saveSchoolRequestApi, { ApiResult, updateSchool as updateSchoolRequest } from "app/shared/model/SaveSchool";
import dayjs, { Dayjs } from "dayjs";
import DateHelper from "app/helpers/dateHelper";
import { getCentreById } from "app/shared/api/getCentreById";
import { saveCentre } from "app/shared/api/saveCentre";

export interface SchoolFormValues {
  id?: string | number;
  schoolName: string;
  schoolCode: string;
  telephoneNo?: string;
  emergencyNo?: string;
  ageFrom?: number;
  ageTo?: number | null;
  lmsAccess?: boolean;
  opensOnSaturday?: boolean;
  opensOnSunday?: boolean;
  isActive?: boolean;
  addressBuilding?: string;
  addressStreet?: string;
  addressCity?: string;
  addressPostalCode?: string;
  addressCountry?: string | number;
  excludedBookingSystemIds?: (string | number)[];
  decommissionDate: Dayjs | null;
  lastActiveSchoolInCentre: boolean;
  entityUpdate: boolean;
}

export type SchoolSaveResult = ApiResult & { savedId?: string | number | null };

export const normalizeSchoolId = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }
  const match = /^(?:School-?|CN)(\d+)$/i.exec(trimmed);
  if (match) {
    return match[1];
  }
  return trimmed;
};

export const toSchoolCacheKey = (value: string | number | null | undefined): string | null => {
  const normalized = normalizeSchoolId(value);
  return normalized != null ? String(normalized) : null;
};

const centreKeyOf = (value: unknown): string | null => {
  if (value == null) return null;

  const key = String(value).trim();
  return key ? key : null;
};

const centresMatch = (left: unknown, right: unknown): boolean => {
  const leftKey = centreKeyOf(left);
  const rightKey = centreKeyOf(right);

  if (!leftKey || !rightKey) return false;
  if (leftKey === rightKey) return true;

  const normalizedLeftKey = normalizeCentreId(leftKey) ?? leftKey;
  const normalizedRightKey = normalizeCentreId(rightKey) ?? rightKey;

  return normalizedLeftKey === normalizedRightKey;
};

export const findCentreBySelectedId = (
    centres: Centre[],
    selectedCentreId: string | number | null
) => {
  return centres.find((centre) => {
    const candidateCentreIds = [
      (centre as any)?.code,
      (centre as any)?.centreCode,
      (centre as any)?.id,
      (centre as any)?.centreId,
    ].filter((candidate) => candidate !== undefined && candidate !== null && candidate !== "");

    return candidateCentreIds.some((candidateId) =>
        centresMatch(candidateId, selectedCentreId)
    );
  });
};

export const normalizeCentreId = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }
  const match = /^CN(\d+)$/i.exec(trimmed);
  if (match) {
    return match[1];
  }
  return trimmed;
};

export const FETCH_CENTRES_REQUEST = "SchoolManager/FETCH_CENTRES_REQUEST";
export const FETCH_CENTRES_SUCCESS = "SchoolManager/FETCH_CENTRES_SUCCESS";
export const FETCH_CENTRES_FAILURE = "SchoolManager/FETCH_CENTRES_FAILURE";

export const FETCH_COUNTRIES_REQUEST = "SchoolManager/FETCH_COUNTRIES_REQUEST";
export const FETCH_COUNTRIES_SUCCESS = "SchoolManager/FETCH_COUNTRIES_SUCCESS";
export const FETCH_COUNTRIES_FAILURE = "SchoolManager/FETCH_COUNTRIES_FAILURE";

export const SET_SELECTED_CENTRE = "SchoolManager/SET_SELECTED_CENTRE";

export const FETCH_SCHOOLS_REQUEST = "SchoolManager/FETCH_SCHOOLS_REQUEST";
export const FETCH_SCHOOLS_SUCCESS = "SchoolManager/FETCH_SCHOOLS_SUCCESS";
export const FETCH_SCHOOLS_FAILURE = "SchoolManager/FETCH_SCHOOLS_FAILURE";

export const SET_SELECTED_SCHOOL = "SchoolManager/SET_SELECTED_SCHOOL";
export const CLEAR_SELECTED_SCHOOL = "SchoolManager/CLEAR_SELECTED_SCHOOL";

export const FETCH_SCHOOL_DETAIL_REQUEST = "SchoolManager/FETCH_SCHOOL_DETAIL_REQUEST";
export const FETCH_SCHOOL_DETAIL_SUCCESS = "SchoolManager/FETCH_SCHOOL_DETAIL_SUCCESS";
export const FETCH_SCHOOL_DETAIL_FAILURE = "SchoolManager/FETCH_SCHOOL_DETAIL_FAILURE";

export const SAVE_SCHOOL_REQUEST = "SchoolManager/SAVE_SCHOOL_REQUEST";
export const SAVE_SCHOOL_SUCCESS = "SchoolManager/SAVE_SCHOOL_SUCCESS";
export const SAVE_SCHOOL_FAILURE = "SchoolManager/SAVE_SCHOOL_FAILURE";

export const SET_FORM_MODE_CREATE = "SchoolManager/SET_FORM_MODE_CREATE";
export const SET_FORM_MODE_EDIT = "SchoolManager/SET_FORM_MODE_EDIT";

const fetchCentresRequest = () => ({ type: FETCH_CENTRES_REQUEST } as const);
const fetchCentresSuccess = (centres: Centre[]) => ({ type: FETCH_CENTRES_SUCCESS, payload: centres } as const);
const fetchCentresFailure = (error: string) => ({ type: FETCH_CENTRES_FAILURE, error } as const);

const fetchCountriesRequest = () => ({ type: FETCH_COUNTRIES_REQUEST } as const);
const fetchCountriesSuccess = (countries: Country[]) => ({ type: FETCH_COUNTRIES_SUCCESS, payload: countries } as const);
const fetchCountriesFailure = (error: string) => ({ type: FETCH_COUNTRIES_FAILURE, error } as const);

const fetchSchoolsRequest = () => ({ type: FETCH_SCHOOLS_REQUEST } as const);
const fetchSchoolsSuccess = (schools: School[]) => ({ type: FETCH_SCHOOLS_SUCCESS, payload: schools } as const);
const fetchSchoolsFailure = (error: string) => ({ type: FETCH_SCHOOLS_FAILURE, error } as const);

const fetchSchoolDetailRequest = (schoolId: string | number) => ({ type: FETCH_SCHOOL_DETAIL_REQUEST, payload: schoolId } as const);
const fetchSchoolDetailSuccess = (school: School) => ({ type: FETCH_SCHOOL_DETAIL_SUCCESS, payload: school } as const);
const fetchSchoolDetailFailure = (schoolId: string | number, error: string) => ({ type: FETCH_SCHOOL_DETAIL_FAILURE, payload: { schoolId, error } } as const);

const saveSchoolRequest = () => ({ type: SAVE_SCHOOL_REQUEST } as const);
const saveSchoolSuccess = (school: School | null, newId?: string | number) => {
  const normalizedNewId = newId != null ? toSchoolCacheKey(newId) ?? String(newId) : undefined;
  const normalizedSchool = school
    ? ({
        ...school,
        id: normalizeSchoolId(school.id ?? (normalizedNewId ?? undefined)) ?? school.id,
      } as School)
    : null;
  return { type: SAVE_SCHOOL_SUCCESS, payload: { school: normalizedSchool, newId: normalizedNewId } } as const;
};
const saveSchoolFailure = (error: string) => ({ type: SAVE_SCHOOL_FAILURE, error } as const);

export const setSelectedCentre = (centreId: string | number | null) => ({ type: SET_SELECTED_CENTRE, payload: centreId } as const);
export const setSelectedSchool = (schoolId: string | number | null) => ({ type: SET_SELECTED_SCHOOL, payload: schoolId } as const);
export const clearSelectedSchool = () => ({ type: CLEAR_SELECTED_SCHOOL } as const);
export const setFormModeCreate = () => ({ type: SET_FORM_MODE_CREATE } as const);
export const setFormModeEdit = () => ({ type: SET_FORM_MODE_EDIT } as const);

type AppThunk<T = any> = (dispatch: Dispatch<any>, getState: () => any) => Promise<T> | T;

const selectSlice = (state: any) =>
  state?.SchoolManager ||
  state?.schoolManager ||
  state?.pages?.SchoolManager ||
  state?.pages?.schoolManager ||
  state?.modules?.SchoolManager ||
  state?.modules?.schoolManager ||
  {};

export const loadCentres = (): AppThunk => async (dispatch) => {
  dispatch(fetchCentresRequest());
  try {
    const centres = await getAllCentres();
    dispatch(fetchCentresSuccess(centres || []));
  } catch (error: any) {
    dispatch(fetchCentresFailure(error?.message || "Failed to load centres"));
  }
};

export const loadCountries = (): AppThunk => async (dispatch) => {
  dispatch(fetchCountriesRequest());
  try {
    const countries = await getAllCountries();
    dispatch(fetchCountriesSuccess(countries || []));
  } catch (error: any) {
    dispatch(fetchCountriesFailure(error?.message || "Failed to load countries"));
  }
};

export const loadSchoolsForCentre = (centreId: string | number): AppThunk => async (dispatch) => {
  dispatch(fetchSchoolsRequest());
  try {
    const schools = await getSchoolsByCentre(centreId);
    dispatch(fetchSchoolsSuccess(schools || []));
  } catch (error: any) {
    dispatch(fetchSchoolsFailure(error?.message || "Failed to load schools"));
  }
};

export const selectCentreAndLoad = (centreId: string | number | null): AppThunk => async (dispatch) => {
  dispatch(setSelectedCentre(centreId));
  dispatch(setFormModeCreate());
  dispatch(clearSelectedSchool());
  if (centreId == null || centreId === "") {
    dispatch(fetchSchoolsSuccess([]));
    return;
  }
  await Promise.resolve(dispatch(loadSchoolsForCentre(centreId) as any));
};

export const selectSchoolForEdit = (schoolId: string | number | null): AppThunk => async (dispatch, getState) => {
  if (schoolId == null || schoolId === "") {
    dispatch(clearSelectedSchool());
    dispatch(setFormModeCreate());
    return;
  }

  const cacheKey = toSchoolCacheKey(schoolId);
  if (!cacheKey) {
    dispatch(clearSelectedSchool());
    dispatch(setFormModeCreate());
    return;
  }

  dispatch(setSelectedSchool(cacheKey));
  dispatch(setFormModeEdit());

  dispatch(fetchSchoolDetailRequest(cacheKey));
  try {
    const slice = selectSlice(getState()) || {};
    const cached = slice.schoolDetails?.[cacheKey] as School | undefined;
    
    let baseSchool: School;
    if (cached) {
      baseSchool = cached;
    } else {
      const school = await getSchoolById(cacheKey);
      baseSchool = {
        ...school,
        id: normalizeSchoolId(school.id ?? cacheKey) ?? school.id,
      };
    }

    let excludedIds: string[] = [];
    try {
      const rawIds = await getExcludedBookingSystemIdsForSchool(cacheKey);
      excludedIds = Array.isArray(rawIds) ? rawIds.map((id: number | string) => String(id)) : [];
    } catch (error) {
      excludedIds = baseSchool.excludedBookingSystemIds ?? [];
    }

    const normalizedSchool: School = {
      ...baseSchool,
      id: normalizeSchoolId(baseSchool.id ?? cacheKey) ?? baseSchool.id,
      excludedBookingSystemIds: excludedIds,
    };
    dispatch(fetchSchoolDetailSuccess(normalizedSchool));
  } catch (error: any) {
    dispatch(fetchSchoolDetailFailure(cacheKey, error?.message || "Failed to load school details"));
  }
};

const buildSavePayload = (
  values: SchoolFormValues,
  centre: Centre | undefined,
  centreId: string | number | null,
  schoolId?: string | number | null
): SaveSchoolParams => {
  const trim = (value?: string) => {
    const next = typeof value === "string" ? value.trim() : value;
    return next === "" ? undefined : next;
  };

  const trimContact = (value?: string) => {
    if (value === undefined || value === null) return undefined;
    return typeof value === "string" ? value.trim() : undefined;
  };

  const normalizedId = normalizeSchoolId(schoolId ?? values.id);

  const centreCode = centre?.code ?? (typeof centreId === "string" ? String(centreId) : undefined);
  const centreIdentifier =
    (centre as any)?.id ??
    (centre as any)?.centreId ??
    centre?.code ??
    centreId ??
    centreCode;
  const normalizedCentreId = normalizeCentreId(centreIdentifier);

  const countryValue = values.addressCountry;

  const payload: SaveSchoolParams = {
    id: normalizedId ?? undefined,
    schoolCode: trim(values.schoolCode) || "",
    name: trim(values.schoolName) || "",
    centreCode,
    centreId: normalizedCentreId ?? undefined,
    telephoneNo: trimContact(values.telephoneNo),
    emergencyNo: trimContact(values.emergencyNo),
    ageFrom: Number(values.ageFrom ?? 0),
    ageTo: values.ageTo === null || values.ageTo === undefined || (typeof values.ageTo === "string" && values.ageTo === "") ? null : Number(values.ageTo),
    lmsAccess: !!values.lmsAccess,
    opensOnSaturday: !!values.opensOnSaturday,
    opensOnSunday: !!values.opensOnSunday,
    isActive: values.isActive !== false,
    line1: trim(values.addressBuilding),
    line2: trim(values.addressStreet),
    line3: trim(values.addressCity),
    line4: trim(values.addressPostalCode),
    countryId:
        countryValue != null && countryValue !== ""
            ? (/^\d+$/.test(String(countryValue)) ? Number(countryValue) : String(countryValue))
            : undefined,
    decommissionDate: values.decommissionDate 
      ? (dayjs.isDayjs(values.decommissionDate) 
          ? values.decommissionDate.format('YYYY-MM-DD') 
          : DateHelper.ToDayJS(values.decommissionDate as any).format('YYYY-MM-DD'))
      : null
  };

  return payload;
};

const toDayjsOrNull = (value: unknown): Dayjs | null => {
  if (value == null || value === "") return null;
  if (dayjs.isDayjs(value)) return value;
  try {
    const parsed = DateHelper.ToDayJS(value as any);
    return parsed?.isValid?.() ? parsed : null;
  } catch {
    return null;
  }
};

const buildOptimisticSchoolFromPayload = (
  existing: School | undefined,
  payload: SaveSchoolParams,
  resolvedId: string | null,
  values: SchoolFormValues
): School => {
  const existingAddress = (existing as any)?.address ?? {};
  const address = {
    ...existingAddress,
    line1: payload.line1 ?? existingAddress.line1,
    line2: payload.line2 ?? existingAddress.line2,
    line3: payload.line3 ?? existingAddress.line3,
    line4: payload.line4 ?? existingAddress.line4,
    countryId: payload.countryId ?? existingAddress.countryId,
  };

  return {
    ...(existing as any),
    id: normalizeSchoolId(existing?.id ?? resolvedId ?? payload.id) ?? (existing?.id ?? resolvedId ?? payload.id),
    code: payload.schoolCode ?? (existing?.code as any),
    name: payload.name ?? (existing?.name as any),
    centreCode: payload.centreCode ?? existing?.centreCode,
    centreId: payload.centreId ?? existing?.centreId,
    telephoneNo: payload.telephoneNo ?? existing?.telephoneNo ?? existing?.telephone,
    emergencyNo: payload.emergencyNo ?? existing?.emergencyNo ?? existing?.emergencyTelephone,
    ageFrom: payload.ageFrom ?? existing?.ageFrom ?? existing?.minimumAge,
    ageTo: payload.ageTo ?? existing?.ageTo ?? null,
    hasLmsAccess: payload.lmsAccess ?? existing?.hasLmsAccess,
    opensOnSaturday: payload.opensOnSaturday ?? existing?.opensOnSaturday,
    opensOnSunday: payload.opensOnSunday ?? existing?.opensOnSunday,
    isActive: payload.isActive ?? existing?.isActive,
    address,
    excludedBookingSystemIds: Array.isArray(values.excludedBookingSystemIds)
      ? values.excludedBookingSystemIds.map((x) => String(x))
      : (existing?.excludedBookingSystemIds ?? []),
    decommissionDate: toDayjsOrNull(payload.decommissionDate),
    lastActiveSchoolInCentre: existing?.lastActiveSchoolInCentre ?? false,
    entityUpdate: existing?.entityUpdate ?? false,
  } as School;
};

const isSchoolInactive = (school: School): boolean => {
  if (school.isActive === false) {
    return true;
  }
  
  if (school.decommissionDate) {
    try {
      const decommissionDate = school.decommissionDate;
      if (decommissionDate.isValid()) {
        const today = DateHelper.ToDayJS(new Date());
        if (today && today.isValid()) {
          if (decommissionDate.isBefore(today, 'day') || decommissionDate.isSame(today, 'day')) {
            return true;
          }
        }
      }
    } catch (error) {
      console.warn("Failed to validate decommissionDate:", error);
    }
  }
  
  return false;
};

const checkAndDeactivateCentreIfNeeded = async (
  centreId: string | number | null,
  normalizedCentreId: string | null
): Promise<void> => {
  if (!centreId || !normalizedCentreId) {
    return;
  }

  try {
    const schools = await getSchoolsByCentre(centreId);
    
    if (!schools || schools.length === 0) {
      return;
    }

    const allSchoolsInactive = schools.every(isSchoolInactive);
    
    if (allSchoolsInactive) {
      const centreIdNumber = parseInt(normalizedCentreId, 10);
      if (isNaN(centreIdNumber)) {
        return;
      }

      const centreDetails = await getCentreById(centreIdNumber);
      
      if (centreDetails.isActive) {
        await saveCentre({
          id: centreIdNumber,
          name: centreDetails.name,
          countryCode: centreDetails.countryCode,
          currencyCode: centreDetails.currencyCode,
          centreCode: centreDetails.centreCode,
          isActive: false,
          physicalCentre: centreDetails.physicalCentre,
          printFormat: centreDetails.printFormat,
          generalEmail: centreDetails.generalEmail,
          accommodationEmail: centreDetails.accommodationEmail,
          telephone: centreDetails.telephone,
          emergencyTelephone: centreDetails.emergencyTelephone,
          schoolSponsorshipNumber: centreDetails.schoolSponsorshipNumber,
          vatNumber: centreDetails.vatNumber,
          registrationNumber: centreDetails.registrationNumber,
          vatExemptionNumber: centreDetails.vatExemptionNumber,
          chequePayableTo: centreDetails.chequePayableTo,
          address: centreDetails.address,
          logo: centreDetails.logo,
          contacts: centreDetails.contacts,
          bankDetails: centreDetails.bankDetails,
        });
      }
    }
  } catch (error) {
    console.error("Failed to check/deactivate centre:", error);
  }
};

export const submitSchoolForm = (values: SchoolFormValues): AppThunk<Promise<ApiResult>> => async (dispatch, getState) => {
  dispatch(saveSchoolRequest());
  try {
    const state = getState();
    const slice = selectSlice(state) || {};
    const centres: Centre[] = slice.centres?.data || slice.centres || [];
    const centreId: string | number | null = slice.selectedCentreId ?? null;

    const centre = findCentreBySelectedId(centres, centreId);

    if (centreId == null && !centre) {
      const error = "Select a centre before saving";
      dispatch(saveSchoolFailure(error));
      return { ok: false, message: error };
    }

    const isEdit = slice.formMode === "edit";
    const selectedSchoolIdRaw = slice.selectedSchoolId ?? null;
    const selectedSchoolIdNormalized = normalizeSchoolId(selectedSchoolIdRaw);
    const payload = buildSavePayload(values, centre, centreId, isEdit ? selectedSchoolIdNormalized : undefined);

    const result = isEdit ? await updateSchoolRequest(payload) : await saveSchoolRequestApi(payload);

    if (!result?.ok) {
      const errorMessage = result?.errors && result.errors.length > 0
        ? result.errors[0]
        : (result?.message || "Failed to save school");
      dispatch(saveSchoolFailure(errorMessage));
      return { ok: false, message: errorMessage, errors: result?.errors, savedId: undefined };
    }

    const resolvedIdRaw = result.data?.id ?? payload.id ?? selectedSchoolIdNormalized ?? null;
    const resolvedId = normalizeSchoolId(resolvedIdRaw);

    const savedSchoolData = result.data?.school as School | undefined;
    const normalizedSavedSchool = savedSchoolData
      ? ({
          ...savedSchoolData,
          id: normalizeSchoolId(savedSchoolData.id ?? resolvedId ?? payload.id) ?? savedSchoolData.id,
        } as School)
      : null;

    const cacheKey = resolvedId ?? toSchoolCacheKey(payload.id) ?? selectedSchoolIdNormalized ?? null;
    const cachedExisting: School | undefined =
      cacheKey != null ? (slice.schoolDetails?.[cacheKey] as School | undefined) : undefined;

    const optimisticSchool =
      normalizedSavedSchool ??
      buildOptimisticSchoolFromPayload(cachedExisting, payload, resolvedId, values);

    dispatch(saveSchoolSuccess(optimisticSchool, resolvedId ?? undefined));

    const centreIdentifier =
      (centre as any)?.id ??
      (centre as any)?.centreId ??
      centre?.code ??
      centreId ??
      centre?.code;
    const normalizedCentreIdForCheck = normalizeCentreId(centreIdentifier);

    if (centreId != null) {
      await checkAndDeactivateCentreIfNeeded(centreId, normalizedCentreIdForCheck);
    }

    return {
      ...result,
      savedId: resolvedId ?? result?.data?.id ?? payload.id ?? selectedSchoolIdNormalized ?? null,
    };
  } catch (error: any) {
    const message = error?.message || "Failed to save school";
    dispatch(saveSchoolFailure(message));
    return { ok: false, message, savedId: undefined };
  }
};

export type SchoolManagerAction =
  | ReturnType<typeof fetchCentresRequest>
  | ReturnType<typeof fetchCentresSuccess>
  | ReturnType<typeof fetchCentresFailure>
  | ReturnType<typeof fetchCountriesRequest>
  | ReturnType<typeof fetchCountriesSuccess>
  | ReturnType<typeof fetchCountriesFailure>
  | ReturnType<typeof setSelectedCentre>
  | ReturnType<typeof fetchSchoolsRequest>
  | ReturnType<typeof fetchSchoolsSuccess>
  | ReturnType<typeof fetchSchoolsFailure>
  | ReturnType<typeof setSelectedSchool>
  | ReturnType<typeof clearSelectedSchool>
  | ReturnType<typeof fetchSchoolDetailRequest>
  | ReturnType<typeof fetchSchoolDetailSuccess>
  | ReturnType<typeof fetchSchoolDetailFailure>
  | ReturnType<typeof saveSchoolRequest>
  | ReturnType<typeof saveSchoolSuccess>
  | ReturnType<typeof saveSchoolFailure>
  | ReturnType<typeof setFormModeCreate>
  | ReturnType<typeof setFormModeEdit>;